import CustomLoading from '@/components/custom-loading';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useCrmCompanyQuery } from '@/gql/generated';
import { formatGiftCard } from '@/lib/utils';
import { OctagonX, X } from 'lucide-react';
import { useForm } from 'react-hook-form';

interface CheckGiftCardProps {
  giftCardCode: string;
  onCheck: (code: string) => void;
  whmcsId: number;
}

const CheckGiftCard = ({
  giftCardCode,
  onCheck,
  whmcsId,
}: CheckGiftCardProps) => {
  const { register, handleSubmit, watch, setValue } = useForm({
    defaultValues: {
      giftCardCode: formatGiftCard(giftCardCode),
    },
  });

  const crmCompanyQuery = useCrmCompanyQuery({
    input_obj: { whmcs_id: whmcsId },
  });

  const giftCardValue = watch('giftCardCode');

  const onSubmit = (data: { giftCardCode: string }) => {
    onCheck(data.giftCardCode.replace(/\s/g, ''));
  };

  const clearGiftCard = () => {
    setValue('giftCardCode', '');
  };

  if (crmCompanyQuery.isLoading) {
    return (
      <div className="fixed left-0 top-0 size-full bg-slate-100/80">
        <div className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
          <CustomLoading />
        </div>
      </div>
    );
  }

  if (crmCompanyQuery.isError) {
    return (
      <div className="min-h-[50vh] text-center flex items-center justify-center">
        <div className="rounded-lg p-8 flex flex-col items-center justify-center gap-4">
          <OctagonX className="size-12 text-red-500" />
          <div className="space-y-2">
            <h3 className="text-xl font-bold text-red-700">Error Occurred</h3>
            <p className="text-red-600">Unable to fetch company data</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <Card className="p-4 md:p-6 font-bold text-xl transition-all mx-auto mb-4 md:mb-6 text-center h-20 flex items-center justify-center">
        {crmCompanyQuery.data?.crm_company?.name}
      </Card>
      <Card className="p-4 md:p-6 transition-all mx-auto mb-4 md:mb-6">
        <form onSubmit={handleSubmit(onSubmit)}>
          <Label htmlFor="code-number" className="text-xl font-bold">
            Check My Balance
          </Label>
          <div className="flex flex-col md:flex-row gap-4 pt-2">
            <div className="relative flex-1">
              <Input
                {...register('giftCardCode', {
                  onChange: (e) => {
                    e.target.value = formatGiftCard(e.target.value);
                  },
                })}
                id="code-number"
                type="text"
                value={formatGiftCard(giftCardValue)}
                className="h-10 md:h-12 pr-8"
                placeholder="Gift Card Code"
                inputMode="numeric"
              />
              {giftCardValue && (
                <Button
                  type="button"
                  variant="ghost"
                  onClick={clearGiftCard}
                  className="absolute right-1 top-1/2 -translate-y-1/2"
                >
                  <X className="size-5" />
                </Button>
              )}
            </div>
            <Button
              disabled={!giftCardValue}
              type="submit"
              className="h-10 md:h-12"
            >
              Check
            </Button>
          </div>
        </form>
      </Card>
    </>
  );
};

export default CheckGiftCard;
